import { Box, InputLabel, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Close, CloudUpload, Description } from '@material-ui/icons';
import { platformTokens } from '@roc/feature-app-core';
import { FileUpload, IconButton } from '@roc/ui';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { GeneralTasksAttachmentStore } from '../../../stores/generalTasksAttachmentStore';

// tokens are read inside makeStyles (not at module load) because of a circular import with feature-app-core
const useStyles = makeStyles((theme: Theme) => {
  const { color, radius, shadow, status } = platformTokens;
  return createStyles({
    inputLabel: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      color: '#222222',
    },
    dropArea: {
      border: `1px dashed ${color.input}`,
      backgroundColor: color.neutral50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      padding: theme.spacing(3, 2),
      borderRadius: radius.xl,
      color: color.subtle,
      cursor: 'pointer',
      outline: 'none',
      transition: 'border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease',
      '&:hover': {
        borderColor: color.inputHover,
        backgroundColor: status.info.bg,
      },
      '&:focus-visible': {
        borderColor: color.ring,
        boxShadow: `0 0 0 4px ${color.ringShadow}`,
      },
    },
    dropAreaActive: {
      borderColor: color.ring,
      backgroundColor: status.info.bg,
      boxShadow: `0 0 0 4px ${color.ringShadow}`,
    },
    icon: {
      color: color.ring,
      fontSize: 28,
    },
    previewList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    preview: {
      position: 'relative',
      width: 96,
      height: 96,
      border: `1px solid ${color.neutral200}`,
      borderRadius: radius.md,
      boxShadow: shadow.sm,
      overflow: 'hidden',
      backgroundColor: color.surface,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    previewImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    previewFile: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(1),
      overflow: 'hidden',
      color: color.subtle,
    },
    previewFileName: {
      width: '100%',
      textAlign: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: 11,
    },
    removeButton: {
      position: 'absolute',
      top: 4,
      right: 4,
      padding: 2,
      borderRadius: radius.pill,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      boxShadow: shadow.sm,
      '&:hover': {
        backgroundColor: color.surface,
      },
      '& svg': {
        fontSize: 16,
      },
    },
  });
});

const pad = (value: number) => `${value}`.padStart(2, '0');

// Clipboard images all arrive named "image.png", which makes several pasted
// screenshots indistinguishable once they are attached to the task.
const buildScreenshotName = (file: File) => {
  const now = new Date();
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} at ${pad(now.getHours())}.${pad(now.getMinutes())}.${pad(now.getSeconds())}`;
  const extension = file.type.split('/')[1] || 'png';
  return `Screenshot ${stamp}.${extension}`;
};

const AttachmentPreview = ({ upload, onRemove }: {
  upload: FileUpload;
  onRemove: () => void;
}) => {
  const classes = useStyles();
  const isImage = upload.file.type.startsWith('image/');
  const previewUrl = useMemo(
    () => (isImage ? URL.createObjectURL(upload.file) : null),
    [upload.file, isImage]
  );

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <Box className={classes.preview}>
      {isImage && previewUrl ? (
        <img className={classes.previewImage} src={previewUrl} alt={upload.name} />
      ) : (
        <Box className={classes.previewFile}>
          <Description />
          <span className={classes.previewFileName} title={upload.name}>
            {upload.name}
          </span>
        </Box>
      )}
      <IconButton
        testId="remove-attachment"
        size="small"
        className={classes.removeButton}
        onClick={onRemove}
      >
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );
};

type TaskAttachmentsFieldProps = {
  attachmentStore: GeneralTasksAttachmentStore;
  /** Only listen for pastes while the create-task dialog is on screen. */
  active: boolean;
};

export const TaskAttachmentsField = observer(({
  attachmentStore,
  active,
}: TaskAttachmentsFieldProps) => {
  const classes = useStyles();
  const { pendingAttachments } = attachmentStore;

  const addFiles = useCallback((files: File[], nameFromClipboard = false) => {
    const uploads = files.map(file => ({
      name: nameFromClipboard ? buildScreenshotName(file) : file.name,
      file,
    }));
    if (uploads.length > 0) {
      attachmentStore.addPendingAttachments(uploads);
    }
  }, [attachmentStore]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: files => addFiles(files),
  });

  useEffect(() => {
    if (!active) return;

    const onPaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.items ?? [])
        .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
        .map(item => item.getAsFile())
        .filter((file): file is File => !!file);

      if (files.length > 0) {
        // Stop the image from also reaching the draft-js description editor.
        event.preventDefault();
        event.stopPropagation();
        addFiles(files, true);
      }
    };

    // Capture phase, so this runs before the description editor's own handler.
    window.addEventListener('paste', onPaste, true);
    return () => window.removeEventListener('paste', onPaste, true);
  }, [active, addFiles]);

  return (
    <>
      <InputLabel className={classes.inputLabel}>Attachments</InputLabel>
      {pendingAttachments.length > 0 && (
        <Box className={classes.previewList}>
          {pendingAttachments.map((upload, index) => (
            <AttachmentPreview
              key={`${upload.name}-${index}`}
              upload={upload}
              onRemove={() => attachmentStore.removePendingAttachment(index)}
            />
          ))}
        </Box>
      )}
      <Box
        {...getRootProps()}
        className={`${classes.dropArea} ${isDragActive ? classes.dropAreaActive : ''}`}
      >
        <input {...getInputProps()} />
        <CloudUpload className={classes.icon} />
        <Typography variant="body2" color="inherit">
          Paste a screenshot (Ctrl/Cmd + V), drag files here, or click to browse
        </Typography>
      </Box>
    </>
  );
});
